import {Attributes, Button, ButtonGroup, DataStatus} from 'platform/components';
import {Heading, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {useGetOrderItemForMaterialQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {CreateCoreExchangeContext} from '../../types/CreateCoreExchangeContext';

interface CreateCoreExchangeDialogProps extends RequiredTestIdProps {
  context: CreateCoreExchangeContext;
  onClose: () => void;
}

/**
 * Content of the dialog opened by `openCreateCoreExchangeDialog`. First step of the core
 * exchange creation: shows the MAIN item the action was triggered on. Selecting the CORE item
 * (`core-exchange-core-item-select` grid) and submitting the core exchange are added by
 * T20-129257.
 */
export function CreateCoreExchangeDialog(props: CreateCoreExchangeDialogProps) {
  const {
    data: mainItem,
    isLoading,
    isError,
  } = useGetOrderItemForMaterialQuery({
    serviceCaseId: props.context.serviceCaseId,
    serviceOrderId: props.context.serviceOrderId,
    serviceJobId: props.context.serviceJobId,
    serviceItemId: props.context.serviceOrderJobItemId,
  });

  const quantity = isNil(mainItem?.quantity?.amount)
    ? null
    : `${mainItem.quantity.amount} ${mainItem.quantity.unit ?? ''}`.trim();

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      {/* 204: the item was removed or moved meanwhile - show the empty state, not blank fields. */}
      <DataStatus isLoading={isLoading} isError={isError} isEmpty={isNil(mainItem)}>
        <VStack spacing={4}>
          <Heading size={4}>{i18n.t('entity.warehouse.labels.coreExchangeMainItem')}</Heading>
          <Attributes
            rows={[
              {label: i18n.t('general.labels.name'), value: mainItem?.name},
              {label: i18n.t('entity.warehouse.labels.catalogueNumber'), value: mainItem?.number},
              {label: i18n.t('general.labels.quantity'), value: quantity},
            ]}
            data-testid={suffixTestId('mainItem', props)}
          />
        </VStack>
      </DataStatus>
      <ButtonGroup align="right">
        <Button
          variant="secondary"
          onClick={props.onClose}
          data-testid={suffixTestId('close', props)}
          title={i18n.t('general.actions.close')}
        />
      </ButtonGroup>
    </VStack>
  );
}
